/**
 * Named hourly labor rates (#2891).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

export interface LaborRate {
  id: string;
  name: string;
  rateCents: number;
  rateGrossCents: number | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export interface CreateLaborRateData {
  name: string;
  rateCents?: number;
  rateGrossCents?: number;
  isDefault?: boolean;
  isActive?: boolean;
}

export type UpdateLaborRateData = Partial<CreateLaborRateData>;

export function useLaborRates(filters: { isActive?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.laborRates.list(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', '100');
      params.set('sortBy', 'name');
      if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive));
      return api.get<PaginatedResponse<LaborRate>>(`/labor-rates?${params.toString()}`);
    },
    staleTime: 30 * 1000,
  });
}

function invalidateLaborRates(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.laborRates.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.tenant.settings() });
}

export function useCreateLaborRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLaborRateData) => api.post<LaborRate>('/labor-rates', data),
    onSuccess: () => invalidateLaborRates(queryClient),
  });
}

export function useUpdateLaborRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLaborRateData }) =>
      api.patch<LaborRate>(`/labor-rates/${id}`, data),
    onSuccess: () => invalidateLaborRates(queryClient),
  });
}

export function useDeleteLaborRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/labor-rates/${id}`),
    onSuccess: () => invalidateLaborRates(queryClient),
  });
}
